import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Heart, Users, Ban, Send, Loader2, Bell, Briefcase } from 'lucide-react';

// Import sub-components
import FavoritesTab from './favorites/FavoritesTab';
import GroupsTab from './favorites/GroupsTab';
import BlocksTab from './favorites/BlocksTab';
import InvitationsTab from './favorites/InvitationsTab';
import { 
  CreateGroupDialog, 
  InviteDialog, 
  PriorityDialog, 
  DirectBookDialog 
} from './favorites/FavoritesDialogs';

const FavoritesPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('favorites');
  const [favorites, setFavorites] = useState([]);
  const [groups, setGroups] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDesc, setNewGroupDesc] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteMode, setInviteMode] = useState(null);
  const [inviteGroupId, setInviteGroupId] = useState(null);
  const [selectedTalent, setSelectedTalent] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [selectedShifts, setSelectedShifts] = useState([]);
  const [inviteMessage, setInviteMessage] = useState('');
  const [inviting, setInviting] = useState(false);
  const [sentInvitations, setSentInvitations] = useState([]);
  
  // Priority notification state
  const [showPriorityDialog, setShowPriorityDialog] = useState(false);
  const [priorityShiftId, setPriorityShiftId] = useState('');
  const [priorityWindowHours, setPriorityWindowHours] = useState(4);
  const [sendingPriority, setSendingPriority] = useState(false);

  // Direct booking state
  const [showDirectBookDialog, setShowDirectBookDialog] = useState(false);
  const [directBookShiftId, setDirectBookShiftId] = useState('');
  const [directBookTalentIds, setDirectBookTalentIds] = useState([]);
  const [directBooking, setDirectBooking] = useState(false);

  // Smart Recommendations state
  const [topPicks, setTopPicks] = useState([]);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);

  const baseUrl = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');
  const headers = { 
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json'
  };

  useEffect(() => {
    fetchData();
    fetchTopPicks();
  }, []);

  const fetchData = async () => {
    try {
      const [favRes, groupsRes, blocksRes, shiftRes] = await Promise.all([
        fetch(`${baseUrl}/api/client/favorites`, { headers }),
        fetch(`${baseUrl}/api/client/groups`, { headers }),
        fetch(`${baseUrl}/api/client/blocks`, { headers }),
        fetch(`${baseUrl}/api/client/shifts`, { headers }),
      ]);
      
      const [favData, groupsData, blocksData, shiftData] = await Promise.all([
        favRes.json(),
        groupsRes.json(),
        blocksRes.json(),
        shiftRes.json(),
      ]);
      
      setFavorites(Array.isArray(favData) ? favData : []);
      setGroups(Array.isArray(groupsData) ? groupsData : []);
      setBlocks(Array.isArray(blocksData) ? blocksData : []);
      setShifts(Array.isArray(shiftData) ? shiftData.filter(s => s.status === 'open') : []);
    } catch (e) {
      console.error('Error fetching data:', e);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchTopPicks = async () => {
    setLoadingRecommendations(true);
    try {
      const res = await fetch(`${baseUrl}/api/recommendations/favorites/top-picks`, { headers });
      if (res.ok) {
        const data = await res.json();
        setTopPicks(data.gigs || []);
      }
    } catch (e) {
      console.error('Error fetching recommendations:', e);
    } finally {
      setLoadingRecommendations(false);
    }
  };

  const fetchInvitations = async () => {
    try {
      const res = await fetch(`${baseUrl}/api/invitations/sent`, { headers });
      if (res.ok) {
        const data = await res.json();
        setSentInvitations(Array.isArray(data) ? data : []);
      }
    } catch (e) {
      console.error('Error fetching invitations:', e);
    }
  };

  // Handlers
  const removeFavorite = async (talentId) => {
    try {
      await fetch(`${baseUrl}/api/client/favorites/${talentId}`, {
        method: 'DELETE',
        headers,
      });
      setFavorites(favorites.filter(f => f.talent_id !== talentId));
      toast.success('Removed from favorites');
    } catch (e) {
      toast.error('Failed to remove favorite');
    }
  };

  const createGroup = async () => {
    if (!newGroupName.trim()) {
      toast.error('Group name is required');
      return;
    }
    try {
      const res = await fetch(`${baseUrl}/api/client/groups`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ name: newGroupName, description: newGroupDesc }),
      });
      if (res.ok) {
        toast.success('Group created');
        setShowCreateGroup(false);
        setNewGroupName('');
        setNewGroupDesc('');
        fetchData();
      }
    } catch (e) {
      toast.error('Failed to create group');
    }
  };

  const deleteGroup = async (groupId) => {
    if (!window.confirm('Delete this group?')) return;
    try {
      await fetch(`${baseUrl}/api/client/groups/${groupId}`, {
        method: 'DELETE',
        headers,
      });
      setGroups(groups.filter(g => g.id !== groupId));
      toast.success('Group deleted');
    } catch (e) {
      toast.error('Failed to delete group');
    }
  };

  const unblockTalent = async (talentId) => {
    try {
      await fetch(`${baseUrl}/api/client/blocks/${talentId}`, {
        method: 'DELETE',
        headers,
      });
      setBlocks(blocks.filter(b => b.talent_id !== talentId));
      toast.success('Talent unblocked');
    } catch (e) {
      toast.error('Failed to unblock talent');
    }
  };

  const toggleTalentSelection = (talentId) => {
    setSelectedTalent(prev => 
      prev.includes(talentId) 
        ? prev.filter(id => id !== talentId)
        : [...prev, talentId]
    );
  };

  const openInviteDialog = (mode, groupId = null) => {
    setInviteMode(mode);
    setInviteGroupId(groupId);
    setSelectedShifts([]);
    setInviteMessage('');
    setShowInviteDialog(true);
  };

  const sendInvites = async () => {
    if (selectedShifts.length === 0) {
      toast.error('Select at least one shift');
      return;
    }
    
    setInviting(true);
    try {
      let talentIds = [];
      if (inviteMode === 'favorites') {
        talentIds = favorites.map(f => f.talent_id);
      } else if (inviteMode === 'selected') {
        talentIds = selectedTalent;
      } else if (inviteMode === 'group' && inviteGroupId) {
        const group = groups.find(g => g.id === inviteGroupId);
        talentIds = group?.members?.map(m => m.talent_id) || [];
      }

      for (const shiftId of selectedShifts) {
        await fetch(`${baseUrl}/api/client/invitations/bulk`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            shift_id: shiftId,
            talent_ids: talentIds,
            message: inviteMessage,
            source: inviteMode === 'group' ? 'group' : 'favorites',
          }),
        });
      }

      toast.success(`Invitations sent to ${talentIds.length} talent for ${selectedShifts.length} shift(s)`);
      setShowInviteDialog(false);
      setSelectedTalent([]);
      fetchInvitations();
    } catch (e) {
      toast.error('Failed to send invitations');
    } finally {
      setInviting(false);
    }
  };

  const sendPriorityNotification = async () => {
    if (!priorityShiftId) {
      toast.error('Select a gig');
      return;
    }
    
    setSendingPriority(true);
    try {
      const res = await fetch(`${baseUrl}/api/client/favorites/priority-notification`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          shift_id: priorityShiftId,
          window_hours: priorityWindowHours,
        }),
      });
      
      if (res.ok) {
        toast.success('Priority notification sent to all favorites!');
        setShowPriorityDialog(false);
        setPriorityShiftId('');
      } else {
        throw new Error('Failed');
      }
    } catch (e) {
      toast.error('Failed to send priority notification');
    } finally {
      setSendingPriority(false);
    }
  };

  const handleDirectBook = async () => {
    if (!directBookShiftId || directBookTalentIds.length === 0) {
      toast.error('Select a gig and talent');
      return;
    }
    
    setDirectBooking(true);
    try {
      const res = await fetch(`${baseUrl}/api/client/direct-book`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          shift_id: directBookShiftId,
          talent_ids: directBookTalentIds,
        }),
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Successfully booked ${data.booked_count} talent!`);
        setShowDirectBookDialog(false);
        setDirectBookTalentIds([]);
        setDirectBookShiftId('');
      } else {
        const err = await res.json();
        throw new Error(err.detail || 'Failed');
      }
    } catch (e) {
      toast.error(e.message || 'Failed to direct book');
    } finally {
      setDirectBooking(false);
    }
  };

  // Tab change handler
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'invitations') {
      fetchInvitations();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#03DFFC]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="favorites-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex flex-wrap items-center justify-between gap-y-2 gap-x-3">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="shrink-0">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="min-w-0">
              <h1 className="text-lg font-semibold text-gray-900 truncate">Favorites & Groups</h1>
              <p className="text-xs text-gray-500 truncate">Manage your preferred talent</p>
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowPriorityDialog(true)}
              className="border-yellow-300 text-yellow-700 hover:bg-yellow-50"
              data-testid="priority-notify-btn"
            >
              <Bell className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline">Priority Alert</span>
            </Button>
            {selectedTalent.length > 0 && (
              <Button
                size="sm"
                onClick={() => {
                  setDirectBookTalentIds(selectedTalent);
                  setShowDirectBookDialog(true);
                }}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <Briefcase className="w-4 h-4 sm:mr-1" />
                <span className="hidden sm:inline">Direct Book </span>({selectedTalent.length})
              </Button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-1 -mb-px overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
            {[
              { id: 'favorites', icon: Heart, label: 'Favorites', count: favorites.length },
              { id: 'groups', icon: Users, label: 'Groups', count: groups.length },
              { id: 'blocks', icon: Ban, label: 'Blocked', count: blocks.length },
              { id: 'invitations', icon: Send, label: 'Invitations' },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`px-4 py-2.5 text-sm font-medium flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap shrink-0 ${
                  activeTab === tab.id
                    ? 'border-[#03DFFC] text-[#03DFFC]'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
                {tab.count !== undefined && (
                  <Badge variant="secondary" className="text-xs px-1.5 py-0">
                    {tab.count}
                  </Badge>
                )}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {activeTab === 'favorites' && (
          <FavoritesTab
            favorites={favorites}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            selectedTalent={selectedTalent}
            toggleTalentSelection={toggleTalentSelection}
            removeFavorite={removeFavorite}
            openInviteDialog={openInviteDialog}
            setShowDirectBookDialog={setShowDirectBookDialog}
            setDirectBookTalentIds={setDirectBookTalentIds}
            setDirectBookShiftId={setDirectBookShiftId}
            topPicks={topPicks}
            loadingRecommendations={loadingRecommendations}
            navigate={navigate}
          />
        )}

        {activeTab === 'groups' && (
          <GroupsTab
            groups={groups}
            setShowCreateGroup={setShowCreateGroup}
            deleteGroup={deleteGroup}
            openInviteDialog={openInviteDialog}
            navigate={navigate}
          />
        )}

        {activeTab === 'blocks' && (
          <BlocksTab
            blocks={blocks}
            unblockTalent={unblockTalent}
          />
        )}

        {activeTab === 'invitations' && (
          <InvitationsTab
            sentInvitations={sentInvitations}
            onRefresh={fetchInvitations}
          />
        )}
      </div>

      {/* Dialogs */}
      <CreateGroupDialog
        open={showCreateGroup}
        onOpenChange={setShowCreateGroup}
        newGroupName={newGroupName}
        setNewGroupName={setNewGroupName}
        newGroupDesc={newGroupDesc}
        setNewGroupDesc={setNewGroupDesc}
        createGroup={createGroup}
      />

      <InviteDialog
        open={showInviteDialog}
        onOpenChange={setShowInviteDialog}
        inviteMode={inviteMode}
        shifts={shifts}
        selectedShifts={selectedShifts}
        setSelectedShifts={setSelectedShifts}
        inviteMessage={inviteMessage}
        setInviteMessage={setInviteMessage}
        sendInvites={sendInvites}
        inviting={inviting}
        selectedTalentCount={selectedTalent.length}
        favoritesCount={favorites.length}
      />

      <PriorityDialog
        open={showPriorityDialog}
        onOpenChange={setShowPriorityDialog}
        shifts={shifts}
        priorityShiftId={priorityShiftId}
        setPriorityShiftId={setPriorityShiftId}
        priorityWindowHours={priorityWindowHours}
        setPriorityWindowHours={setPriorityWindowHours}
        sendPriorityNotification={sendPriorityNotification}
        sendingPriority={sendingPriority}
        favoritesCount={favorites.length}
      />

      <DirectBookDialog
        open={showDirectBookDialog}
        onOpenChange={setShowDirectBookDialog}
        shifts={shifts}
        directBookShiftId={directBookShiftId}
        setDirectBookShiftId={setDirectBookShiftId}
        directBookTalentIds={directBookTalentIds}
        handleDirectBook={handleDirectBook}
        directBooking={directBooking}
      />
    </div>
  );
};

export default FavoritesPage;
